import { useEffect, useRef, useState } from 'react';
import { findExistingTopic, getCategoryMeta, TOPIC_MAX_LENGTH, type ExistingTopic } from '@brainfart/shared';
import { Sheet, SheetBody } from './Sheet';
import { AlertsOptIn } from './AlertsOptIn';
import {
  TopicRequestError, fetchPopularTopics, requestTopic, voteForTopic,
  type TopicSuggestion, type TopicVoteResult,
} from '../topicRequests';
import { track } from '../telemetry';

// "Don't see your topic? Request it" — a link that opens a small popup where
// you type a topic or back one someone already asked for. Everyone who asked
// is told when it launches.
export function RequestTopicLink({ where, style }: { where: string; style?: React.CSSProperties }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="btn btn-ghost"
        onClick={() => {
          track({ type: 'topic_request_opened', payload: { where } });
          setOpen(true);
        }}
        style={{ fontSize: '0.9rem', padding: '0.6rem 1rem', ...style }}
      >
        💡 Don't see your topic? <span style={{ color: '#FFC93C', marginLeft: '0.25rem' }}>Request it</span>
      </button>
      {open && <TopicRequestSheet where={where} onClose={() => setOpen(false)} />}
    </>
  );
}

function TopicRequestSheet({ where, onClose }: { where: string; onClose: () => void }) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<TopicSuggestion[]>([]);
  const [existing, setExisting] = useState<ExistingTopic | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<TopicVoteResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Suggestions follow what's being typed (popular ones when empty).
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      fetchPopularTopics(text.trim()).then(s => { if (!cancelled) setSuggestions(s); }).catch(() => {});
    }, text ? 250 : 0);
    setExisting(text.trim().length >= 3 ? findExistingTopic(text) : null);
    return () => { cancelled = true; clearTimeout(t); };
  }, [text]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handle = async (action: () => Promise<TopicVoteResult>, kind: 'topic_requested' | 'topic_voted') => {
    setSaving(true);
    setError(null);
    try {
      const result = await action();
      track({ type: kind, payload: { where, votes: result.votes, topic: result.topic } });
      setDone(result);
    } catch (err) {
      const status = err instanceof TopicRequestError ? err.status : 0;
      setError(status === 429 ? "That's plenty for today. Thanks! Try again tomorrow."
        : status === 400 ? 'Try a real topic name, like "Formula 1" or "Harry Potter".'
          : "Couldn't send that. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    if (!text.trim() || saving) return;
    handle(() => requestTopic(text), 'topic_requested');
  };

  if (done) {
    const launched = done.launched ? getCategoryMeta(done.launched) : null;
    const others = done.votes - 1;
    return (
      <Sheet open onClose={onClose} label="Topic requested">
        <SheetBody
          icon={launched ? '🎉' : '🙌'}
          title={launched ? `${launched.title} is already live!` : done.alreadyVoted ? "You've already asked for this" : 'Request sent!'}
          body={launched
            ? `Find it in the topic list.`
            : others > 0
              ? `You and ${others} other ${others === 1 ? 'player' : 'players'} want ${done.topic}. The most-wanted topics get built first, and we'll tell you when it's live.`
              : `You're the first to ask for ${done.topic}. The most-wanted topics get built first, and we'll tell you when it's live.`}
        >
          {!launched && <AlertsOptIn where="topic_request" prompt="🔔 Notify me when it launches" doneText="🔔 We'll ping you the moment it's live" />}
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </SheetBody>
      </Sheet>
    );
  }

  const existingMeta = existing ? getCategoryMeta(existing.category) : null;

  return (
    <Sheet open onClose={onClose} label="Request a topic">
      <div style={{ textAlign: 'left' }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#FFC93C', marginBottom: '0.25rem', textAlign: 'center' }}>💡 Request a topic</h2>
        <p style={{ color: '#aaa', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1rem' }}>
          What should we add next? The most-wanted topics get built first.
        </p>
        <form onSubmit={e => { e.preventDefault(); submit(); }} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value.slice(0, TOPIC_MAX_LENGTH))}
            placeholder="e.g. Formula 1, Harry Potter…"
            maxLength={TOPIC_MAX_LENGTH}
            enterKeyHint="send"
            style={{ flex: 1, minWidth: 0, padding: '0.8rem 1rem', borderRadius: '12px', border: '1px solid #2E2B45', background: '#100E22', color: '#fff', fontSize: '1rem', outline: 'none' }}
          />
          <button type="submit" className="btn btn-primary" disabled={!text.trim() || saving} style={{ padding: '0.8rem 1rem', width: 'auto', flexShrink: 0 }}>
            {saving ? '…' : 'Request'}
          </button>
        </form>

        {existingMeta && (
          <p style={{ color: '#39FF88', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            {existing?.theme
              ? `✅ "${existing.theme}" is already a theme in ${existingMeta.icon} ${existingMeta.title}. Pick that topic to play it.`
              : `✅ ${existingMeta.icon} ${existingMeta.title} is already here. Pick it from the list!`}
          </p>
        )}
        {error && <p style={{ color: '#FF6B6B', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}

        {suggestions.length > 0 && (
          <>
            <p style={{ color: '#888', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', margin: '0.25rem 0 0.4rem' }}>
              {text.trim() ? 'ALREADY REQUESTED' : 'MOST WANTED'}
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {suggestions.map(s => (
                <li key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', borderRadius: '10px', background: '#1B1830', border: '1px solid #2E2B45' }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{s.topic}</span>
                  <span style={{ color: '#888', fontSize: '0.8rem', flexShrink: 0 }}>▲ {s.votes}</span>
                  <button
                    className={s.voted ? 'btn btn-ghost' : 'btn btn-secondary'}
                    disabled={s.voted || saving}
                    onClick={() => handle(() => voteForTopic(s.id), 'topic_voted')}
                    style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem', flexShrink: 0, width: 'auto' }}
                  >
                    {s.voted ? '✓ Asked' : 'Me too'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Sheet>
  );
}
